import React from 'react';
import clsx from 'clsx';
import { AccountCircle } from '@mui/icons-material';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { SummaryTable } from './SummaryTable';
import { HighestResult, ProposalSummary } from '../../../dashboard/ProposalsMetricsDashboard';
import { SummaryTabStyledContainer } from './SummaryTabStyledContainer';

interface Props {
  isLoading: boolean;
  proposalSummary: ProposalSummary[];
  summaryHighestResultData: HighestResult | null;
}

export const SummaryTab = ({ proposalSummary, isLoading, summaryHighestResultData }: Props) => (
  <SummaryTabStyledContainer>
    <Stack gap="1rem">
      <Typography variant="h6" component="span">
        Summary
      </Typography>

      <RenderWithCondition
        condition={!!summaryHighestResultData}
        fallBack={
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', height: '256px', gap: '1rem' }}>
            {new Array(4).fill('').map((_, index) => (
              <Skeleton key={index} height="100%" width="100%" variant="rectangular" />
            ))}
          </Box>
        }
      >
        <Box className="summaryCards">
          <Box className="summaryCardsLine">
            {Object.entries(summaryHighestResultData?.bestUsers ?? {}).map(([category, data]) => (
              <Box key={category} className={clsx(isLoading && 'proposalsSummaryCard-loading', 'usersCard')}>
                <Stack justifyContent="space-between">
                  <Typography fontWeight="bold" component="span" color="success">
                    {data.label}
                  </Typography>

                  <Stack direction="row" alignItems="center" gap="0.5rem">
                    <AccountCircle color="success" />
                    <Typography color="success">{data.userFullName ?? '-'}</Typography>
                  </Stack>
                </Stack>

                <Typography color="success" variant="h6">
                  {data.value ?? '-'}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box className="summaryCardsLine">
            {Object.entries(summaryHighestResultData?.totals?.byCategory ?? {}).map(([category, data]) => (
              <Box key={category} className={clsx(isLoading && 'proposalsSummaryCard-loading', 'totalsCard')}>
                <Stack direction="row" justifyContent="space-between">
                  <Stack>
                    <Typography variant="subtitle1" component="span" color="textDisabled">
                      {data.label}
                    </Typography>

                    <Typography variant="h6" component="span">
                      {data.amount}
                    </Typography>
                  </Stack>

                  <Box className="totalsCardIconWrapper">{data.icon}</Box>
                </Stack>
                <Box className="totalsCardInfo">
                  <Box className="totalsCardInfoItem">
                    <Typography color="textDisabled">Markup</Typography>
                    <Typography variant="h6" component="span">
                      {data.markup}
                    </Typography>
                  </Box>

                  <Box className="totalsCardInfoItem">
                    <Typography color="textDisabled">Active deals</Typography>
                    <Typography variant="h6" component="span">
                      {data.activeDeals}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}

            <Stack gap="0.5rem" justifyContent="space-between" height="100%">
              {Object.values(summaryHighestResultData?.totals?.createdByProposalsType ?? {}).map(({ label, value }) => (
                <Box
                  key={label}
                  className={clsx(isLoading && 'proposalsSummaryCard-loading', 'createdByProposalsTypeCard')}
                >
                  <Typography component="span" variant="subtitle1" color="textDisabled">
                    {label}
                  </Typography>

                  <Typography variant="h6" component="span">
                    {value ?? '-'}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      </RenderWithCondition>
    </Stack>

    <SummaryTable data={proposalSummary} isLoading={isLoading} />
  </SummaryTabStyledContainer>
);
